import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { PdfViewer } from 'components/PdfViewer';
import ResolveImage from 'components/ResolveImage';
import { Modal, Loader } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import { isPdf, isVideo, isImage } from 'utils';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { ModalProps } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { FilePreview } from 'models/File';
import CannotPreviewInBrowser from 'components/CannotPreviewInBrowser';
import usePreviewModalNavigation from 'hooks/usePreviewModalNavigation';
import PreviewModalNavigationButtons from 'components/PreviewModalNavigationButtons';
import PreviewModalDownloadButton from 'components/PreviewModalDownloadButton';
import useMatchMedia from 'hooks/useMatchMedia';

import './FilePreviewModal.styles.scss';

type FilePreviewModalProps = {
  className?: string;
  isUrlFetching?: boolean;
  /**
   * Download readiness comes from the owner, not from `url` — `url` is the rendered
   * preview (object url), which only exists once the whole file is downloaded, while
   * downloading needs nothing but the signed url.
   */
  isDownloadDisabled?: boolean;
  /** stable file identity, so the download cooldown resets once per file */
  downloadResetKey?: string;
  onDownload?: () => void;
  onPrev?: () => void;
  onNext?: () => void;
} & FilePreview &
  ModalProps;

const FilePreviewModal: ForwardRefRenderFunction<
  OverlayRef,
  FilePreviewModalProps
> = (props, ref) => {
  const {
    className,
    url,
    type,
    isUrlFetching,
    isDownloadDisabled,
    downloadResetKey,
    onDownload,
    onPrev,
    onNext,
    ...rest
  } = props;

  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const isPhablet = useMatchMedia('isPhablet');

  const classes = classNames(
    'preview-file-modal-template',
    'file-preview-modal',
    className,
  );

  const { t } = useTranslation();

  const { canNavigate } = usePreviewModalNavigation({
    modal,
    onPrev,
    onNext,
  });

  const canPreviewInBrowser = isPdf(type) || isVideo(type) || isImage(type);

  const renderDownloadButton = useMemo(
    () => (
      <PreviewModalDownloadButton
        shouldRender={!!onDownload}
        disabled={isDownloadDisabled}
        resetKey={downloadResetKey}
        onDownload={onDownload}
      />
    ),
    [onDownload, isDownloadDisabled, downloadResetKey],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      lockScroll={false}
      title={
        !canPreviewInBrowser || !renderDownloadButton ? (
          <span style={{ minHeight: isPhablet ? '34px' : '42px' }} />
        ) : (
          renderDownloadButton
        )
      }
      {...rest}
    >
      {isUrlFetching && <Loader />}

      <PreviewModalNavigationButtons
        canNavigate={canNavigate}
        onPrev={onPrev}
        onNext={onNext}
      />

      {url &&
        (isPdf(type) ? (
          <PdfViewer url={url} />
        ) : isVideo(type) ? (
          <video src={url} controls controlsList="nodownload" />
        ) : isImage(type) ? (
          <ResolveImage src={url} />
        ) : (
          <CannotPreviewInBrowser
            title={`${t('thisFileTypeCantBePreviewedInTheBrowser')}.`}
            description={t('downloadTheFileToViewIt')}
            showDescription={!!renderDownloadButton}
            actions={renderDownloadButton}
          />
        ))}
    </Modal>
  );
};

export default forwardRef(FilePreviewModal);
